import * as THREE from 'three';
import { SectionConfig, SectionRect, SectionInterface, SectionInsets } from '../types';
import { createSash } from '../geometry/SashGeometry';
import { createGlassPane } from '../geometry/GlassGeometry';
import { createHandle } from '../geometry/HandleGeometry';
import { createRoedenPattern } from '../geometry/RoedenGeometry';
import { MaterialLibrary } from '../materials/MaterialLibrary';
import { PROFILE } from '../utils/profileConstants';

/**
 * STOLP: Two leaves meeting without a fixed center mullion (stolpraam).
 * Each leaf pivots from its outer edge.
 */
export class StolpSection implements SectionInterface {
  readonly group: THREE.Group;
  private leftPivot: THREE.Group;
  private rightPivot: THREE.Group;

  constructor(config: SectionConfig, rect: SectionRect, materialLib: MaterialLibrary, frameMaterial: THREE.Material, insets: SectionInsets) {
    this.group = new THREE.Group();
    this.group.name = `section-stolp-${rect.index}`;
    this.group.userData = { sectionIndex: rect.index, sectionType: 'STOLP' };

    // Stolp occupies the full section; outer edges use insets, inner meeting edges get 0.
    // Effective total height accounts for top/bottom insets; effective full width for left/right.
    const effTotalW = rect.width - insets.left - insets.right;
    const effH = rect.height - insets.top - insets.bottom;
    const leafW = effTotalW / 2;
    const clearW = leafW - 2 * PROFILE.sashWidth;
    const clearH = effH - 2 * PROFILE.sashWidth;

    const glassMat = materialLib.glass(config.infill.tint, config.infill.glazingTier);
    const handleMat = materialLib.handleMaterial(config.hardware.handleFinish);

    // Left leaf — hinge on left (outer) edge, handle on right (meeting) edge
    const leftAssembly = new THREE.Group();
    leftAssembly.add(createSash(leafW, effH, PROFILE.sashWidth, PROFILE.sashDepth, frameMaterial));
    const leftGlass = createGlassPane(clearW, clearH, PROFILE.glassThickness, glassMat);
    leftGlass.position.set(leafW / 2, effH / 2, 0);
    leftAssembly.add(leftGlass);

    if (config.roeden.pattern !== 'none' && config.roeden.mode !== 'none') {
      const roeden = createRoedenPattern(
        clearW, clearH, config.roeden,
        materialLib.roedenMaterial(),
      );
      roeden.position.set(leafW / 2, effH / 2, 0);
      leftAssembly.add(roeden);
    }

    const leftHandle = createHandle(handleMat);
    leftHandle.position.set(leafW - PROFILE.sashWidth / 2, effH / 2, PROFILE.sashDepth / 2);
    leftAssembly.add(leftHandle);

    // Left pivot at outer (left) edge of sash
    this.leftPivot = new THREE.Group();
    this.leftPivot.position.set(insets.left, 0, 0);
    leftAssembly.position.set(0, insets.bottom, 0);
    this.leftPivot.add(leftAssembly);

    // Right leaf — hinge on right (outer) edge, handle on left (meeting) edge
    const rightAssembly = new THREE.Group();
    rightAssembly.add(createSash(leafW, effH, PROFILE.sashWidth, PROFILE.sashDepth, frameMaterial));
    const rightGlass = createGlassPane(clearW, clearH, PROFILE.glassThickness, glassMat);
    rightGlass.position.set(leafW / 2, effH / 2, 0);
    rightAssembly.add(rightGlass);

    if (config.roeden.pattern !== 'none' && config.roeden.mode !== 'none') {
      const roeden = createRoedenPattern(
        clearW, clearH, config.roeden,
        materialLib.roedenMaterial(),
      );
      roeden.position.set(leafW / 2, effH / 2, 0);
      rightAssembly.add(roeden);
    }

    const rightHandle = createHandle(handleMat);
    rightHandle.position.set(PROFILE.sashWidth / 2, effH / 2, PROFILE.sashDepth / 2);
    rightAssembly.add(rightHandle);

    // Right pivot at outer (right) edge of sash
    this.rightPivot = new THREE.Group();
    this.rightPivot.position.set(insets.left + effTotalW, 0, 0);
    rightAssembly.position.set(-leafW, insets.bottom, 0);
    this.rightPivot.add(rightAssembly);

    this.group.add(this.leftPivot);
    this.group.add(this.rightPivot);

    if (config.openAmount > 0) {
      this.setOpenAngle(config.openAmount * (Math.PI / 2));
    }

    this.group.position.set(rect.x, rect.y, 0);
  }

  setOpenAngle(rad: number): void {
    // Left leaf swings inward (negative Y), right leaf swings inward (positive Y)
    this.leftPivot.rotation.y = -rad;
    this.rightPivot.rotation.y = rad;
  }
}
